/**
 * TagSuggestionCard Component (Phase 23: US14)
 * Displays a tag suggestion with accept/reject actions.
 */

import { Check, X, Loader2, Sparkles, FileText, Image } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { TagSuggestion } from '@/types/api';

interface TagSuggestionCardProps {
  tag: TagSuggestion;
  /** Whether this tag is being processed */
  isProcessing?: boolean;
  /** Whether this tag has been rejected */
  isRejected?: boolean;
  /** Handler for accepting the tag */
  onAccept: () => void;
  /** Handler for rejecting the tag */
  onReject: () => void;
  /** Additional className */
  className?: string;
}

/**
 * TagSuggestionCard displays a single tag suggestion with accept/reject buttons.
 *
 * Features:
 * - Source indicator (text vs image)
 * - Confidence display
 * - AI sparkle indicator
 * - Accept/reject action buttons
 * - Loading state during processing
 */
export function TagSuggestionCard({
  tag,
  isProcessing = false,
  isRejected = false,
  onAccept,
  onReject,
  className,
}: TagSuggestionCardProps): JSX.Element {
  // Format confidence as percentage
  const confidencePercent = tag.confidence !== undefined
    ? `${String(Math.round(tag.confidence * 100))}%`
    : null;

  // Source icon component
  const SourceIcon = tag.source === 'image' ? Image : FileText;
  const sourceLabel = tag.source === 'image'
    ? `From image ${tag.imageIndex !== undefined ? String(tag.imageIndex + 1) : ''}`
    : 'From text analysis';

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1 rounded-md border bg-card px-2 py-1',
        isRejected && 'opacity-50 line-through',
        isProcessing && 'opacity-70',
        className
      )}
      data-testid={`tag-suggestion-card-${String(tag.tagId)}`}
    >
      {/* AI indicator */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Sparkles className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          AI Suggested
        </TooltipContent>
      </Tooltip>

      {/* Tag name */}
      <span className="text-sm font-medium">{tag.tagName}</span>

      {/* Confidence badge */}
      {confidencePercent && (
        <Badge variant="outline" className="h-5 px-1.5 text-[10px]">
          {confidencePercent}
        </Badge>
      )}

      {/* Source indicator */}
      <Tooltip>
        <TooltipTrigger asChild>
          <SourceIcon className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {sourceLabel}
        </TooltipContent>
      </Tooltip>

      {/* Action buttons */}
      <div className="flex items-center gap-0.5 ml-1">
        {isProcessing ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <>
            {/* Accept button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-green-600 hover:text-green-700 hover:bg-green-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAccept();
                  }}
                  disabled={isRejected}
                  data-testid={`accept-tag-${String(tag.tagId)}`}
                >
                  <Check className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                Accept tag
              </TooltipContent>
            </Tooltip>

            {/* Reject button */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-red-600 hover:text-red-700 hover:bg-red-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReject();
                  }}
                  data-testid={`reject-tag-${String(tag.tagId)}`}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                Reject tag
              </TooltipContent>
            </Tooltip>
          </>
        )}
      </div>
    </div>
  );
}
